/**
 * `/` — the landing screen: the caller's cards, and editing them.
 *
 * Every card is a **card type** with a config — core's in `coreCards.tsx`,
 * each module's in its manifest — placed by the rules in `layout.ts`: the
 * saved rows first, then the defaults that have no row.
 *
 * **Customize** turns the cards into placeholders: drag by the handle, choose a
 * width within the type's limits, open the settings, remove, add. Nothing is
 * sent until Save, which replaces the whole layout; Reset sends an empty one,
 * which is the defaults. Customize is offered from `lg` up: on two columns or
 * one, a width means nothing.
 *
 * Each card renders inside an `ErrorBoundary`: a card that crashes renders
 * nothing, and its cell closes.
 */
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import DashboardCustomizeOutlinedIcon from '@mui/icons-material/DashboardCustomizeOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { Theme } from '@mui/material/styles';
import { DASHBOARD_LIMITS } from '@forsys/shared';
import { getDashboardLayout, saveDashboardLayout } from '../../api/dashboard';
import { useAuth } from '../../auth/useAuth';
import { CardGrid, type CardGridItem } from '../../components/CardGrid';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { EmptyState } from '../../components/EmptyState';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { ErrorState } from '../../components/ErrorState';
import { PageHeader } from '../../components/PageHeader';
import { useAsync } from '../../hooks/useAsync';
import { useSubmit } from '../../hooks/useSubmit';
import { MODULE_NAMESPACES } from '../../modules/locales';
import type { DashboardContext } from '../../modules/types';
import { useModules } from '../../modules/useModules';
import { AddCardDialog } from './AddCardDialog';
import { CardSettingsDialog } from './CardSettingsDialog';
import { CORE_DASHBOARD_CARDS } from './coreCards';
import { EditCard } from './EditCard';
import {
  addItem,
  arrangeLayout,
  availableTypes,
  cardCatalogue,
  moveItem,
  removeItem,
  resolveDefaults,
  shownCards,
  toSaved,
  updateItem,
  type CatalogueEntry,
  type DashboardItem,
} from './layout';

export default function DashboardPage() {
  const { t } = useTranslation(['system', 'common', ...MODULE_NAMESPACES]);
  const { user, can, canAny, isSuperadmin } = useAuth();
  const modules = useModules();
  const wide = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));

  // Two keys rather than a substituted "there": "Welcome, there" has no
  // grammatical equivalent in Czech, and forcing one produces nonsense.
  const firstName = user?.displayName.split(' ')[0];

  const ctx = useMemo<DashboardContext>(() => ({ can, canAny }), [can, canAny]);
  const catalogue = useMemo(() => cardCatalogue(CORE_DASHBOARD_CARDS, modules), [modules]);
  const available = useMemo(() => availableTypes(catalogue, ctx), [catalogue, ctx]);

  const saved = useAsync(getDashboardLayout, []);
  const defaults = useAsync(() => resolveDefaults(available, ctx), [available, ctx]);

  const items = useMemo(
    () => (saved.data && defaults.data ? arrangeLayout(saved.data.layout.cards, defaults.data) : undefined),
    [saved.data, defaults.data],
  );

  /** The editor's copy while editing; null otherwise. */
  const [draft, setDraft] = useState<DashboardItem[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [settingsId, setSettingsId] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);
  const added = useRef(0);

  const save = useSubmit(async (cards: DashboardItem[]) => {
    const response = await saveDashboardLayout(toSaved(cards));
    saved.setData(response);
    setDraft(null);
    setResetting(false);
    return response;
  });

  const editing = draft !== null;
  const shown = useMemo(() => shownCards(draft ?? items ?? [], available), [draft, items, available]);
  const settingsCard = shown.find((card) => card.item.id === settingsId) ?? null;

  function begin() {
    if (!items) return;
    save.clearError();
    setDraft([...items]);
  }

  function add(entry: CatalogueEntry) {
    if (!draft) return;
    added.current += 1;
    const id = `added-${added.current}`;
    setDraft(addItem(draft, entry, id));
    setAdding(false);
    if (entry.type.settings?.length) setSettingsId(id);
  }

  const gridItems: CardGridItem[] = shown.map((card) => {
    const title = t(card.entry.type.titleKey);
    const Card = card.entry.type.component;
    return {
      key: card.item.id,
      width: card.width,
      label: title,
      render: (handle) =>
        draft ? (
          <EditCard
            card={card}
            ctx={ctx}
            handle={handle}
            onWidth={(width) => setDraft(updateItem(draft, card.item.id, { width }))}
            onSettings={() => setSettingsId(card.item.id)}
            onRemove={() => setDraft(removeItem(draft, card.item.id))}
          />
        ) : (
          <ErrorBoundary>
            <Card config={card.config} />
          </ErrorBoundary>
        ),
    };
  });

  const full = (draft?.length ?? 0) >= DASHBOARD_LIMITS.CARDS;

  const actions = editing ? (
    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
      <Tooltip title={full ? t('system:dashboard.addCardFull') : ''}>
        <span>
          <Button startIcon={<AddIcon />} disabled={full || save.pending} onClick={() => setAdding(true)}>
            {t('system:dashboard.addCard')}
          </Button>
        </span>
      </Tooltip>
      <Button color="inherit" disabled={save.pending} onClick={() => setResetting(true)}>
        {t('system:dashboard.reset')}
      </Button>
      <Button color="inherit" disabled={save.pending} onClick={() => setDraft(null)}>
        {t('common:action.cancel')}
      </Button>
      <Button variant="contained" disabled={save.pending} onClick={() => draft && void save.submit(draft)}>
        {save.pending ? t('common:action.saving') : t('common:action.save')}
      </Button>
    </Stack>
  ) : wide && items ? (
    <Button startIcon={<DashboardCustomizeOutlinedIcon />} onClick={begin}>
      {t('system:dashboard.customize')}
    </Button>
  ) : undefined;

  const failed = saved.error ?? defaults.error;

  return (
    <>
      <PageHeader
        title={firstName ? t('system:dashboard.welcome', { name: firstName }) : t('system:dashboard.welcomeAnonymous')}
        description={isSuperadmin ? t('system:dashboard.superadminIntro') : t('system:dashboard.intro')}
        actions={actions}
      />

      {editing && (
        <Stack spacing={2} sx={{ mb: 2 }}>
          <Alert severity="info">{t('system:dashboard.editHint')}</Alert>
          {save.error && !resetting && <ErrorState error={save.error} severity="warning" />}
        </Stack>
      )}

      {failed ? (
        <ErrorState
          error={failed}
          onRetry={() => {
            saved.reload();
            defaults.reload();
          }}
        />
      ) : !items ? (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, minmax(0, 1fr))' } }}>
          {Array.from({ length: 3 }, (_, index) => (
            <Skeleton key={index} variant="rounded" height={88} />
          ))}
        </Box>
      ) : shown.length === 0 ? (
        <EmptyState title={t('system:dashboard.nothingHere')} description={t('system:dashboard.nothingHereHint')} />
      ) : (
        <CardGrid
          items={gridItems}
          {...(draft ? { onMove: (from: string, to: string) => setDraft(moveItem(draft, from, to)) } : {})}
        />
      )}

      <AddCardDialog open={adding} types={available} onAdd={add} onClose={() => setAdding(false)} />

      <CardSettingsDialog
        // Remounted per card, so the form starts from that card's config.
        key={settingsId ?? 'closed'}
        card={settingsCard ? { entry: settingsCard.entry, config: settingsCard.config } : null}
        ctx={ctx}
        onSave={(config) => {
          if (draft && settingsId) setDraft(updateItem(draft, settingsId, { config: { ...config } }));
          setSettingsId(null);
        }}
        onClose={() => setSettingsId(null)}
      />

      <ConfirmDialog
        open={resetting}
        title={t('system:dashboard.resetTitle')}
        message={t('system:dashboard.resetMessage')}
        confirmLabel={t('system:dashboard.reset')}
        tone="error"
        pending={save.pending}
        error={save.error}
        onConfirm={() => void save.submit([])}
        onClose={() => {
          save.clearError();
          setResetting(false);
        }}
      />
    </>
  );
}
